import { useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { isThreadError, isThreadUnavailableError, useChat } from '@loadsmart/ai-chat'
import {
  Composer,
  ComposerInput,
  ComposerSubmitButton,
  Conversation,
  ErrorBanner,
  ErrorButton,
  ErrorMessage,
  Message,
  MessageText,
} from '@loadsmart/ai-chat/ui'
import { Icon, type IconName } from '@loadsmart/miranda-react'

import '@loadsmart/ai-chat/style.css'

import { answerText } from 'assistant/domain/answerText'
import { describeChatError, UNAVAILABLE_THREAD } from 'assistant/domain/chatErrors'
import { STARTER_QUESTIONS } from 'assistant/domain/starterQuestions'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { SentryWrapper } from 'core/infra/sentry/SentryWrapper'

import { AgentWork, TypingDots } from './AnswerProgress'
import {
  RingAngleProperty,
  StyledChat,
  StyledComposer,
  StyledComposerBox,
  StyledConversation,
  StyledHeadline,
  StyledHint,
  StyledIntro,
  StyledLede,
  StyledNotice,
  StyledStarter,
  StyledStarterList,
  StyledStarters,
  StyledStop,
} from './AssistantChat.styles'
import { useAssistantBackend } from './hooks/useAssistantBackend'
import { useStopAnswer } from './hooks/useStopAnswer'
import MessageActions from './MessageActions'

export const ASSISTANT_AGENT_ID = 'analytics-agent'

/** What each starter question is about, shown as its icon. */
const STARTER_ICONS: Record<string, IconName> = {
  [STARTER_QUESTIONS[0]]: 'warehouse',
  [STARTER_QUESTIONS[1]]: 'truck',
  [STARTER_QUESTIONS[2]]: 'timer',
  [STARTER_QUESTIONS[3]]: 'calendar',
}

type AssistantChatProps = {
  threadId: string
  /**
   * Whether the thread already exists on the server. Fixed for the life of the
   * conversation: turning `hydrate` on later would re-read the thread and
   * replace what is on screen, in the middle of the first answer.
   */
  isExistingThread: boolean
  /** Called when the user sends a message. */
  onSent?: () => void
  /** Called when an answer has finished, so the page can refresh what depends on it. */
  onAnswerEnded?: () => void
}

export default function AssistantChat({
  threadId,
  isExistingThread,
  onSent,
  onAnswerEnded,
}: AssistantChatProps) {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const backend = useAssistantBackend()
  const [draft, setDraft] = useState('')

  const { messages, status, error, sendMessage, regenerate, hydrateMessages, stop } = useChat({
    backend,
    agent: ASSISTANT_AGENT_ID,
    threadId,
    hydrate: isExistingThread,
    resume: isExistingThread,
    onError: failure => SentryWrapper.log(failure),
  })

  const { stopAnswer, isStopping, notice, clearNotice } = useStopAnswer({ threadId, stop })

  // Whatever starts a new answer (a question, Regenerate, Retry) ends the old notice.
  useEffect(() => {
    if (status === 'submitted') clearNotice()
  }, [status, clearNotice])

  const isBusy = status === 'initializing' || status === 'submitted' || status === 'streaming'

  const previousStatus = useRef(status)

  useEffect(() => {
    const was = previousStatus.current

    previousStatus.current = status

    if ((was === 'submitted' || was === 'streaming') && status === 'ready') onAnswerEnded?.()
  }, [status, onAnswerEnded])

  function ask(text: string) {
    const question = text.trim()

    if (!question || isBusy || isStopping) return

    clearNotice()
    track(AnalyticsEvent.AssistantSendMessage, { Thread: threadId })
    void sendMessage(question)
    onSent?.()
  }

  function handleSubmit() {
    const question = draft

    setDraft('')
    ask(question)
  }

  // The library reads a thread it cannot find as an empty conversation. For a
  // thread that was meant to exist, that is a stale link or a deleted thread.
  const isMissingThread = isExistingThread && status === 'ready' && messages.length === 0
  const failure =
    status === 'error' && error
      ? describeChatError(error)
      : isMissingThread
        ? UNAVAILABLE_THREAD
        : null
  // A thread that cannot be used stays unusable until the user leaves it. Only an
  // answer still being written (409) passes by itself.
  const isThreadUnusable =
    isMissingThread || (status === 'error' && isThreadError(error) && failure?.kind !== 'busy')
  // `regenerate` cannot clear a failed read of the thread; reading it again can.
  const isReadFailure = status === 'error' && isThreadUnavailableError(error)
  const isAnswering = status === 'submitted' || status === 'streaming'
  // The answer the user is looking at last. A newer question below it (a Stop while it was being
  // sent) means Regenerate would answer that question, so the control is not offered then.
  const lastMessage = messages[messages.length - 1]

  return (
    <StyledChat>
      <StyledConversation>
        {messages.length === 0 && status === 'ready' && !isMissingThread ? (
          <StyledStarters>
            <StyledIntro>
              <StyledHeadline>What do you want to know about your data?</StyledHeadline>
              <StyledLede>
                Ask in plain words. Answers come from the data your account can access.
              </StyledLede>
            </StyledIntro>
            <StyledStarterList>
              {STARTER_QUESTIONS.map(question => (
                <li key={question}>
                  <StyledStarter type="button" onClick={() => ask(question)}>
                    <Icon name={STARTER_ICONS[question] ?? 'sparkle'} size="20px" />
                    {question}
                  </StyledStarter>
                </li>
              ))}
            </StyledStarterList>
          </StyledStarters>
        ) : (
          <Conversation>
            {messages.map(message => {
              const isLastAnswer = message.role === 'assistant' && message.id === lastMessage?.id
              // The answer still being written has no row yet, and cannot be regenerated.
              const hasActions =
                message.role === 'assistant' &&
                answerText(message) !== '' &&
                !(isLastAnswer && isAnswering)
              const canRegenerate = isLastAnswer && status === 'ready' && !isThreadUnusable

              return (
                <Message key={message.id} id={message.id} from={message.role}>
                  {message.parts.map((part, index) => {
                    if (part.type === 'text') {
                      return <MessageText key={index}>{part.text}</MessageText>
                    }

                    if (part.type === 'work') {
                      return <AgentWork key={index} state={part.state} steps={part.steps} />
                    }

                    return null
                  })}
                  {hasActions && (
                    <MessageActions
                      threadId={threadId}
                      message={message}
                      canRegenerate={canRegenerate}
                      onRegenerate={regenerate}
                    />
                  )}
                </Message>
              )
            })}
            {status === 'submitted' && <TypingDots />}
          </Conversation>
        )}
      </StyledConversation>

      {failure && (
        <ErrorBanner>
          <ErrorMessage>{failure.message}</ErrorMessage>
          {failure.kind === 'sign-in' && (
            <ErrorButton label="Sign in" onClick={() => navigate('/logout')}>
              <Icon name="log-out" size="16px" />
            </ErrorButton>
          )}
          {failure.kind === 'generic' && (
            <ErrorButton
              label={isReadFailure ? 'Reload' : 'Retry'}
              onClick={isReadFailure ? hydrateMessages : regenerate}
            >
              <Icon name="refresh" size="16px" />
            </ErrorButton>
          )}
          {failure.kind === 'busy' && (
            <ErrorButton
              label="Stop the running answer"
              onClick={async () => {
                await stopAnswer()
                // The error hides the thread; reading it again shows the turn the server kept.
                hydrateMessages()
              }}
            >
              <Icon name="x" size="16px" />
            </ErrorButton>
          )}
          {failure.kind === 'unavailable-thread' && (
            <ErrorButton label="New chat" onClick={() => navigate('/assistant')}>
              <Icon name="plus" size="16px" />
            </ErrorButton>
          )}
        </ErrorBanner>
      )}

      <RingAngleProperty />
      <StyledComposer $isWorking={isAnswering}>
        <StyledComposerBox>
          <Composer onSubmit={handleSubmit}>
            <ComposerInput
              value={draft}
              onChange={setDraft}
              isDisabled={isBusy || isThreadUnusable || isStopping}
              placeholder="Ask a question about your data"
            />
            <ComposerSubmitButton
              isDisabled={isBusy || isThreadUnusable || isStopping || draft.trim() === ''}
            />
          </Composer>
          {isAnswering && (
            <StyledStop
              type="button"
              aria-label="Stop the answer"
              onClick={stopAnswer}
              disabled={isStopping}
            >
              Stop
            </StyledStop>
          )}
        </StyledComposerBox>
        {notice ? (
          <StyledNotice role="status">{notice}</StyledNotice>
        ) : (
          <StyledHint>
            This is experimental. LLMs are prone to hallucination, so check your numbers.
          </StyledHint>
        )}
      </StyledComposer>
    </StyledChat>
  )
}
